<script>
  import { onMount, onDestroy } from 'svelte';
  import BlockMenu from './BlockMenu.svelte';
  import FigureMenu from './FigureMenu.svelte';
  import NotificationBubble from '../../../shared/NotificationBubble.svelte';
  import { blocks, contextualBlocks, getSectionDisplayName } from '../blocks/blockDefinitions.js';
  import { boilerplateService } from '../../../../services/boilerplateService';
  import { referenceService } from '../../../../services/referenceService';
  import { blockMenuStore, figureMenuStore } from '../../../../stores/menuStore';
  import { figuresStore } from '../../../../stores/figuresStore';
  
  export let editor;
  
  // Cleanup function for event handlers
  let cleanup = null;
  let isInitialized = false;

  // Menu states
  let blockMenu = { visible: false, position: null, query: '', items: [], triggerPos: null };
  let figureMenu = { visible: false, position: null, query: '', items: [], triggerPos: null, loading: false, error: null };
  
  // Selected index for keyboard navigation
  let blockSelectedIndex = 0;
  let figureSelectedIndex = 0;
  
  // Submenu state for block menu
  let activeSubmenu = null;
  let selectedSubmenuIndex = 0;
  
  // Duplicate section error notification
  let showDuplicateError = false;
  let duplicateErrorMessage = '';
  
  // Get figures from store once when menu opens, don't reactively update
  $: ({ figures, loading: figuresLoading, error: figuresError } = $figuresStore);

  // Load figures on mount (will check staleness automatically)
  onMount(() => {
    figuresStore.loadFigures();
  });

  function getBlockItems() {
    // Get current section type to determine contextual blocks
    const sectionType = findPrecedingSectionType();
    
    // Start with base blocks
    let allBlocks = [...blocks];
    
    // Process dynamic submenus (boilerplates)
    allBlocks = allBlocks.map(block => {
      // Clone the block to avoid mutating the original
      const processedBlock = {...block};
      
      // Handle dynamic submenu for boilerplates
      if (processedBlock.dynamicSubmenu === 'boilerplates') {
        processedBlock.submenuItems = boilerplateService.getBoilerplateMenuItems();
      }
      
      return processedBlock;
    });
    
    // Add contextual blocks based on section type
    if (sectionType && contextualBlocks[sectionType]) {
      const contextBlocks = contextualBlocks[sectionType].map(block => ({...block}));
      allBlocks = [...allBlocks, ...contextBlocks];
    }
    
    return allBlocks;
  }

  function findPrecedingSectionType() {
    if (!editor) return null;
    
    const { state } = editor;
    const { selection, doc } = state;
    const currentPos = selection.$from.pos;
    
    // Walk backwards through the document until we find a heading
    let precedingHeading = null;
    doc.nodesBetween(0, currentPos, (node, pos, parent) => {
      if (node.type.name === 'heading' && pos < currentPos) {
        precedingHeading = node;
        return false; // Stop traversing once we find a heading
      }
    });
    
    return precedingHeading?.attrs?.sectionType || null;
  }


  // Expose menu trigger functions that can be called by extensions
  export function openBlockMenu(triggerPos) {
    if (!editor) return;
    
    const { state, view } = editor;
    const { selection } = state;
    const { $from } = selection;
    
    // Get the cursor position for the menu coordinates
    const cursorCoords = view.coordsAtPos($from.pos);
    
    // Calculate smart positioning for block menu
    const windowHeight = window.innerHeight;
    const menuHeight = 400; // Max height of menu
    const menuWidth = 280; // Width of menu
    
    let posX = cursorCoords.left;
    let posY = cursorCoords.bottom + 5;
    let flipVertical = false;
    
    if (cursorCoords.bottom + menuHeight > windowHeight) {
      posY = cursorCoords.top - 5;
      flipVertical = true;
    }
    
    // Open block menu
    blockMenu = {
      visible: true,
      position: {
        x: posX,
        y: posY,
        flipVertical,
        cursorY: cursorCoords.top
      },
      query: '',
      items: getBlockItems(),
      triggerPos: triggerPos
    };
    
    blockMenuStore.open(blockMenu.position);
    blockSelectedIndex = 0;
    activeSubmenu = null;
    selectedSubmenuIndex = 0;
  }
  
  export function openFigureMenu(triggerPos) {
    if (!editor) return;
    
    // Don't force refresh when opening menu to prevent scroll jumping
    // figuresStore.refresh();
    
    const { state, view } = editor;
    const { selection } = state;
    const { $from } = selection;
    
    // Get the cursor position for the menu coordinates
    const cursorCoords = view.coordsAtPos($from.pos);
    
    // Calculate smart positioning for figure menu
    const windowHeight = window.innerHeight;
    const menuHeight = 400;
    const menuWidth = 300;
    
    let posX = cursorCoords.left;
    let posY = cursorCoords.bottom + 5;
    let flipVertical = false;
    
    if (cursorCoords.bottom + menuHeight > windowHeight) {
      posY = cursorCoords.top - 5;
      flipVertical = true;
    }
    
    // Capture current store state at menu open time (no reactive updates)
    const currentStoreState = $figuresStore;
    
    // Open figure menu with static snapshot of data
    figureMenu = {
      visible: true,
      position: {
        x: posX,
        y: posY,
        flipVertical,
        cursorY: cursorCoords.top
      },
      query: '',
      items: [...currentStoreState.figures], // Static snapshot, not reactive
      triggerPos: triggerPos,
      loading: currentStoreState.loading,
      error: currentStoreState.error
    };
    
    figureMenuStore.open(figureMenu.position);
    figureSelectedIndex = 0;
  }
  
  export function closeMenus() {
    if (blockMenu.visible) {
      blockMenu.visible = false;
      blockMenu.triggerPos = null;
      blockMenuStore.close();
    }
    if (figureMenu.visible) {
      figureMenu.visible = false;
      figureMenu.triggerPos = null;
      figureMenuStore.close();
    }
  }
  
  function setupTipTapHandlers() {
    if (!editor) {
      return;
    }

    // Setup keyboard handlers for when menus are open
    const handleKeyDown = (event) => {
      // Only handle keyboard events when menus are open
      if (!blockMenu.visible && !figureMenu.visible) {
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();
      
      // Route event to appropriate handler
      if (blockMenu.visible) {
        handleBlockMenuKeyDown(event);
      } else if (figureMenu.visible) {
        handleFigureMenuKeyDown(event);
      }
    };

    // Add event listener for menu keyboard navigation
    if (editor && editor.view && editor.view.dom) {
      editor.view.dom.addEventListener('keydown', handleKeyDown, true);
    }

    // Cleanup function
    return () => {
      if (editor && editor.view && editor.view.dom) {
        editor.view.dom.removeEventListener('keydown', handleKeyDown, true);
      }
    };
  }

  function handleBlockSelect(block) {
    console.log('HANDLEBLOCKSELECT called with:', block);
    
    // Close the menu immediately
    blockMenu.visible = false;
    blockMenuStore.close();

    // Check for duplicate sections before inserting headings
    if (block.command === 'setHeading' && block.attrs?.sectionType) {
      const sectionType = block.attrs.sectionType;
      console.log('CHECKING FOR DUPLICATES of sectionType:', sectionType);
      
      // Check if this section type already exists in the document
      const { state } = editor;
      let sectionExists = false;
      
      state.doc.descendants((node) => {
        if (node.type.name === 'heading' && node.attrs.sectionType === sectionType) {
          console.log('FOUND EXISTING SECTION:', node.attrs.sectionType);
          sectionExists = true;
          return false; // Stop traversing
        }
      });
      
      if (sectionExists) {
        // Show error notification and prevent insertion
        const displayName = getSectionDisplayName(sectionType);
        duplicateErrorMessage = `Only one ${displayName} section is allowed`;
        showDuplicateError = true;
        console.log('DUPLICATE PREVENTION: Showing error for', displayName, 'showDuplicateError=', showDuplicateError);
        console.log('duplicateErrorMessage=', duplicateErrorMessage);
        return;
      } else {
        console.log('NO EXISTING SECTION FOUND, proceeding with insertion');
      }
    } else {
      console.log('NOT A SECTION HEADING, block.command=', block.command, 'block.attrs=', block.attrs);
    }

    // Use a single transaction for all operations
    editor.chain()
      .focus()
      .command(({ tr, state }) => {
        // 1. Delete the "/" character if present
        if (blockMenu.triggerPos !== null) {
          tr.delete(blockMenu.triggerPos, blockMenu.triggerPos + 1);
        }
        
        // 2. Get the current block position after deletion
        const { $from } = tr.selection;
        const currentBlock = $from.parent;
        const blockStart = $from.before($from.depth);
        const blockEnd = $from.after($from.depth);
        
        // 3. Apply the appropriate transformation based on block type
        if (block.command === 'setHeading') {
          
          // For headings, we transform the current block
          if (currentBlock.type.name === 'paragraph' && currentBlock.content.size === 0) {
            // Empty paragraph: directly transform to heading
            tr.setNodeMarkup(blockStart, state.schema.nodes.heading, block.attrs);
          } else if (currentBlock.type.name === 'paragraph') {
            // Non-empty paragraph: transform to heading
            tr.setNodeMarkup(blockStart, state.schema.nodes.heading, block.attrs);
          } else {
            // If not in a paragraph, insert a new heading after current block
            const heading = state.schema.nodes.heading.create(block.attrs);
            tr.insert(blockEnd, heading);
            tr.setSelection(state.selection.constructor.near(tr.doc.resolve(blockEnd + 1)));
          }
          
        } else if (block.command === 'setParagraph') {
          
          // For regenerative blocks, transform the current paragraph
          if (currentBlock.type.name === 'paragraph') {
            // Update attributes of current paragraph
            tr.setNodeMarkup(blockStart, null, block.attrs);
          } else {
            // If not in a paragraph, insert a new paragraph after current block
            const paragraph = state.schema.nodes.paragraph.create(block.attrs);
            tr.insert(blockEnd, paragraph);
            tr.setSelection(state.selection.constructor.near(tr.doc.resolve(blockEnd + 1)));
          }
          
        } else if (block.command === 'setBoilerplate') {
          
          // For boilerplate, we need to handle content insertion differently
          // Boilerplate service will handle the actual content insertion
          // but we should prepare the position
          
          if (currentBlock.type.name === 'paragraph' && currentBlock.content.size === 0) {
            // Empty paragraph: delete it first (boilerplate will create its own paragraphs)
            tr.delete(blockStart, blockEnd);
          }
        }
        
        return true;
      })
      .run();
    
    // For boilerplate, handle content insertion after the transaction
    if (block.command === 'setBoilerplate') {
      boilerplateService.insertBoilerplate(editor, block.attrs.name, block.attrs.content);
    }
  }
  

  function handleFigureSelect(item) {
    // Close the menu
    figureMenu.visible = false;
    // Update the store
    figureMenuStore.close();
    
    // Remove the @ trigger character since insertReference will add its own @
    if (figureMenu.triggerPos !== null) {
      editor.chain()
        .command(({ tr }) => {
          // Delete the @ at triggerPos
          tr.delete(figureMenu.triggerPos, figureMenu.triggerPos + 1);
          return true;
        })
        .run();
    }
    
    // Use the unified insertion method for consistency
    referenceService.insertReference(editor, item);
  }

  function handleBlockMenuKeyDown(event) {
    // Prevent default to avoid text insertion while menu is open
    event.preventDefault();
    
    // Handle query updates (character input)
    // Exclude '/' from being added to query since it's the trigger character
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && event.key !== '/') {
      blockMenu = { ...blockMenu, query: blockMenu.query + event.key };
      // Reset selected index when query changes
      blockSelectedIndex = 0;
      return;
    }
    
    // Handle backspace in query
    if (event.key === 'Backspace') {
      if (blockMenu.query.length > 0) {
        blockMenu = { ...blockMenu, query: blockMenu.query.slice(0, -1) };
        // Reset selected index when query changes
        blockSelectedIndex = 0;
      } else {
        // Close menu if query is empty and backspace is pressed
        blockMenu.visible = false;
        blockMenu.triggerPos = null; // Reset trigger position
        blockMenuStore.close();
      }
      return;
    }
    
    // Handle submenu navigation if active
    if (activeSubmenu) {
      handleSubmenuKeyDown(event);
      return;
    }

    // Handle navigation in main menu
    // Replicate the same filtering logic as BlockMenu component
    const filteredItems = (() => {
      // If no query, show normal hierarchical structure
      if (!blockMenu.query) {
        return blockMenu.items;
      }
      
      // With a query, flatten the structure to show matching submenu items directly
      const results = [];
      const lowerQuery = blockMenu.query.toLowerCase();
      
      blockMenu.items.forEach(item => {
        const itemString = `${item.title} ${item.description || ''}`.toLowerCase();
        const itemMatches = itemString.includes(lowerQuery);
        
        // Check if any submenu items match the search
        let hasMatchingSubmenuItems = false;
        let matchingSubmenuItems = [];
        if (item.hasSubmenu && item.submenuItems) {
          matchingSubmenuItems = item.submenuItems.filter(subItem => {
            const subItemString = `${subItem.title} ${subItem.description || ''}`.toLowerCase();
            return subItemString.includes(lowerQuery);
          });
          hasMatchingSubmenuItems = matchingSubmenuItems.length > 0;
        }
        
        // Show the item if either:
        // 1. The parent item matches, OR
        // 2. Any of its submenu items match
        if (itemMatches || hasMatchingSubmenuItems) {
          // Keep the item exactly as-is with its submenu intact
          results.push(item);
        }
        
        // ALSO add matching submenu items as standalone options
        // This allows direct selection when searching submenu content
        matchingSubmenuItems.forEach(subItem => {
          results.push({
            ...subItem,
            _isSubmenuItem: true,
            _parentTitle: item.title
          });
        });
      });
      
      console.log('[MENUMANAGER DEBUG] Final filtered results:', results);
      return results;
    })();
    
    switch (event.key) {
      case 'ArrowDown':
        blockSelectedIndex = (blockSelectedIndex + 1) % filteredItems.length;
        break;
      case 'ArrowUp':
        blockSelectedIndex = (blockSelectedIndex - 1 + filteredItems.length) % filteredItems.length;
        break;
      case 'Enter':
        // Make sure to stop propagation and prevent default
        event.preventDefault();
        event.stopPropagation();
        
        const selectedBlock = filteredItems[blockSelectedIndex];
        // If we're in search mode and this is a flattened submenu item, just select it
        if (blockMenu.query && selectedBlock?._isSubmenuItem) {
          handleBlockSelect(selectedBlock);
        } else if (selectedBlock?.hasSubmenu) {
          // Allow Enter to open submenu even during search
          activeSubmenu = selectedBlock;
          selectedSubmenuIndex = 0;
        } else if (selectedBlock) {
          handleBlockSelect(selectedBlock);
        }
        break;
      case 'ArrowRight':
        const block = filteredItems[blockSelectedIndex];
        if (block?.hasSubmenu) {
          activeSubmenu = block;
          selectedSubmenuIndex = 0;
        }
        break;
      case 'Escape':
        blockMenu.visible = false;
        blockMenu.triggerPos = null; // Reset trigger position
        blockMenuStore.close();
        break;
    }
  }

  function handleSubmenuKeyDown(event) {
    if (!activeSubmenu?.submenuItems?.length) return;
    
    switch (event.key) {
      case 'ArrowDown':
        selectedSubmenuIndex = (selectedSubmenuIndex + 1) % activeSubmenu.submenuItems.length;
        break;
      case 'ArrowUp':
        selectedSubmenuIndex = (selectedSubmenuIndex - 1 + activeSubmenu.submenuItems.length) % activeSubmenu.submenuItems.length;
        break;
      case 'Enter':
        // Make sure to stop propagation and prevent default
        event.preventDefault();
        event.stopPropagation();
        
        
        const selectedItem = activeSubmenu.submenuItems[selectedSubmenuIndex];
        if (selectedItem) {
          handleBlockSelect(selectedItem);
        }
        break;
      case 'ArrowLeft':
      case 'Escape':
        activeSubmenu = null;
        selectedSubmenuIndex = 0;
        break;
    }
  }

  function handleFigureMenuKeyDown(event) {
    // Prevent default to avoid text insertion while menu is open
    event.preventDefault();
    
    // Handle query updates (character input)
    // Exclude '@' from being added to query since it's the trigger character
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && event.key !== '@') {
      figureMenu = { ...figureMenu, query: figureMenu.query + event.key };
      return;
    }
    
    // Handle backspace in query
    if (event.key === 'Backspace') {
      if (figureMenu.query.length > 0) {
        figureMenu = { ...figureMenu, query: figureMenu.query.slice(0, -1) };
      } else {
        // Close menu if query is empty and backspace is pressed
        figureMenu.visible = false;
        figureMenu.triggerPos = null; // Reset trigger position
        figureMenuStore.close();
      }
      return;
    }
    
    // Filter figures based on query
    const filteredFigures = figureMenu.items.filter(figure => {
      return figure.name.toLowerCase().includes(figureMenu.query.toLowerCase());
    });
    
    // Handle navigation
    switch (event.key) {
      case 'ArrowDown':
        figureSelectedIndex = (figureSelectedIndex + 1) % filteredFigures.length;
        break;
      case 'ArrowUp':
        figureSelectedIndex = (figureSelectedIndex - 1 + filteredFigures.length) % filteredFigures.length;
        break;
      case 'Enter':
        // Make sure to stop propagation and prevent default
        event.preventDefault();
        event.stopPropagation();
        
        
        const selectedFigure = filteredFigures[figureSelectedIndex];
        if (selectedFigure) {
          handleFigureSelect(selectedFigure);
        }
        break;
      case 'Escape':
        figureMenu.visible = false;
        figureMenu.triggerPos = null; // Reset trigger position
        figureMenuStore.close();
        break;
    }
  }

  // Re-setup handlers when editor changes after initialization
  $: if (isInitialized && editor) {
    if (cleanup) cleanup();
    cleanup = setupTipTapHandlers();
  }

  // Set up event listeners and return cleanup function
  onMount(() => {
    isInitialized = true;
    
    // The reactive statement below will handle attaching handlers when editor is ready
    // No need for retry mechanism here
    
    return () => {
      if (cleanup) cleanup();
    };
  });
</script>

{#if blockMenu.visible}
  <BlockMenu 
    position={blockMenu.position}
    query={blockMenu.query}
    items={blockMenu.items} 
    selectedIndex={blockSelectedIndex}
    activeSubmenu={activeSubmenu}
    selectedSubmenuIndex={selectedSubmenuIndex}
    on:select={e => handleBlockSelect(e.detail)}
    on:submenuSelect={e => handleBlockSelect(e.detail)}
    on:close={() => {
      blockMenu.visible = false;
      blockMenu.triggerPos = null; // Reset trigger position when closing via click outside
      blockMenuStore.close();
    }}
    on:activateSubmenu={e => {
      activeSubmenu = e.detail;
      selectedSubmenuIndex = 0;
    }}
  />
{/if}

{#if figureMenu.visible}
  <FigureMenu 
    position={figureMenu.position}
    query={figureMenu.query}
    items={figureMenu.items.filter(figure => 
      figure.name.toLowerCase().includes(figureMenu.query.toLowerCase())
    )}
    selectedIndex={figureSelectedIndex}
    loading={figureMenu.loading}
    error={figureMenu.error}
    on:select={e => handleFigureSelect(e.detail)}
    on:indexChange={e => figureSelectedIndex = e.detail}
    on:close={() => {
      figureMenu.visible = false;
      figureMenu.triggerPos = null; // Reset trigger position when closing via click outside
      figureMenuStore.close();
    }}
  />
{/if}

<!-- Duplicate section error notification -->
{#if showDuplicateError}
  <div class="duplicate-prevention-warning-container">
    <NotificationBubble 
      show={true}
      message={duplicateErrorMessage}
      variant="error"
      position="top"
      duration={2000}
      on:hide={() => showDuplicateError = false}
    />
  </div>
{/if}

<style>
  .duplicate-prevention-warning-container {
    position: fixed;
    top: 140px;
    left: 19%;
    z-index: 99999;
    display: flex;
    flex-direction: column;
    align-items: center;
    transform: translateX(-50%);
    pointer-events: none;
    gap: var(--spacing-2);
  }
</style>